---
title: "How you get paid"
description: "The marketplace money mechanics in one place: the placement fee, the platform's 20% cut, the winner-take-all recruiter share, and the finalist bonus."
category: "marketplace"
keywords:
  - "recruiter fees"
  - "placement fee split"
  - "platform cut"
  - "finalist bonus"
  - "recruiter payout"
schema: "Article"
nav:
  section: "guides"
  order: 46
  parent: "/guides/marketplace"
---

This is the one page with all the numbers. Everything a role card or earnings screen shows you traces back to here.

## The placement fee

Each role publishes its fee when the company lists it — anywhere from 5% to 100% of the candidate's first-year base, and most land between 15% and 25%. The company pays it when your candidate starts. A published fee can be raised but never lowered, so the percentage you saw on the card is at least the percentage that applies.

## The split

The platform takes 20% of the placement fee. The placing recruiter gets the other 80%. That's the entire structure.

Worked example on a $200,000 base with a 20% fee:

| | |
| --- | --- |
| Placement fee (paid by the company) | $40,000 |
| Platform (20% of the fee) | $8,000 |
| You (80% of the fee) | $32,000 |

The recruiter share is winner-take-all: one recruiter places the candidate, that recruiter gets the full share. There's no pool, no fractional credit, no splitting with whoever else worked the role. Role cards and your earnings page show your number — the $32,000 — because that's the figure you're deciding with; the split above is where it comes from.

## The finalist bonus

Getting a candidate to the final interview is worth money even when someone else's candidate wins the offer. Each of your candidates that reaches a role's final interview stage earns you a bonus — currently **$200 per finalist** — the moment they enter the stage. Paid up front, hired or not, and it stays yours either way.

The company defines which stage counts as final when it publishes the role, so "final interview" is never a judgment call. One bonus per candidate per role. If a candidate jumps straight to an offer, the final-interview rung still counts as reached.

## When money moves

- **Finalist bonus** — payable immediately when the candidate enters the final stage. It shows in your earnings the same minute.
- **Placement fee share** — held in escrow at offer acceptance, then released once the company's fee reaches Chosen in cleared funds.

Chosen can only pay out a fee it has actually collected, so the placement share follows the company's payment rather than the start date. Once the fee clears, your share is released in four installments:

| Installment | Released |
| --- | --- |
| One sixth | Within 3 business days of the fee clearing |
| One sixth | After day 30 |
| One third | After day 60 |
| One third | After day 90 |

The schedule exists because Chosen owes the company a replacement guarantee for a while after the hire. The moment that guarantee expires, every remaining installment is released within ten business days — you don't wait out the rest of the clock for a risk that's already gone.

Your earnings page tracks every event with its state, so "where's my money" has a screen instead of an email thread.

## Fall-offs

If a placed candidate leaves inside the company's guarantee period and Chosen has to refund part of the fee, your share is adjusted to match. Installments still owing stop, and if you've already been paid more than your share of what Chosen ends up keeping, you repay the difference.

That repayment is always proportional to what was actually refunded, and it can never exceed what you were paid for that placement.

You owe nothing at all when the refund is Chosen's fault, or when the company eliminated the role, ran a layoff, or ended the employment for reasons unrelated to the candidate. Finalist bonuses are never repayable.

The full terms are in the recruiter agreement, readable any time under Settings → Legal.
